import React, { useState, createContext, useContext, ReactNode, } from "react";

interface AccordionContextType {
  expanded: boolean;
  disabled?: boolean;
  toggle: (event: React.SyntheticEvent) => void;
}

const AccordionContext = createContext<AccordionContextType | null>(null);

function useAccordionContext() {
  const context = useContext(AccordionContext);

  if (!context) {
    throw new Error("Componentes do Accordion devem ser usados dentro de um <Accordion>.");
  }

  return context;
}

export interface AccordionProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
  children: NonNullable<ReactNode>;
  defaultExpanded?: boolean;
  disabled?: boolean;
  expanded?: boolean;
  onChange?: (event: React.SyntheticEvent, expanded: boolean) => void;
}

export function Accordion({
  children,
  defaultExpanded = false,
  disabled = false,
  expanded: controlledExpanded,
  onChange,
  className = "",
  ...props
}: AccordionProps): JSX.Element {
  const isControlled = controlledExpanded !== undefined;
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);

  const expanded = isControlled ? controlledExpanded : internalExpanded;

  const toggle = (event: React.SyntheticEvent) => {
    if (disabled) return;
    const newExpandedState = !expanded;

    if (!isControlled) {
      setInternalExpanded(newExpandedState);
    }

    if (onChange) {
      onChange(event, newExpandedState);
    }
  };

  return (
    <AccordionContext.Provider value={{ expanded, disabled, toggle }}>
      <div
        className={`border-b border-gray-200 bg-white shadow-sm transition-all duration-300 first:rounded-t-md last:rounded-b-md last:border-none ${
          disabled ? "opacity-50 pointer-events-none" : ""
        } ${className}`}
        {...props}
      >
        {children}
      </div>
    </AccordionContext.Provider>
  );
}

export interface AccordionSummaryProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  expandIcon?: ReactNode;
}

export function AccordionSummary({
  children,
  expandIcon,
  className = "",
  onClick,
  ...props
}: AccordionSummaryProps): JSX.Element {
  const { expanded, disabled, toggle } = useAccordionContext();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (onClick) onClick(event);
    toggle(event);
  };

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={handleClick}
      aria-expanded={expanded}
      className={`w-full flex items-center justify-between p-4 text-left transition-colors hover:bg-gray-50 focus:outline-none focus-visible:bg-gray-100 ${className}`}
      {...props}
    >
      <div className="flex-grow">{children}</div>
      {expandIcon && (
        <div
          className={`ml-4 transform transition-transform duration-300 ease-in-out flex-shrink-0 ${
            expanded ? "rotate-180" : "rotate-0"
          }`}
        >
          {expandIcon}
        </div>
      )}
    </button>
  );
}

export interface AccordionDetailsProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
}

export function AccordionDetails({
  children,
  className = "",
  ...props
}: AccordionDetailsProps): JSX.Element {
  const { expanded } = useAccordionContext();

  return (
    <div
      className={`grid transition-all duration-300 ease-in-out ${
        expanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
      }`}
      aria-hidden={!expanded}
    >
      <div className="overflow-hidden">
        <div
          className={`p-4 pt-0 text-gray-600 ${className}`}
          {...props}
        >
          {children}
        </div>
      </div>
    </div>
  );
}